import React, { useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  setCurrentProject,
  addRecentProject,
  removeRecentProject,
} from '@/store/slices/projectSlice';
import { openFolderAndCreateProject } from '@/utils/projectUtils';
import { toast } from 'sonner';
import { toggleProjectSelector } from '@/store/slices/uiSlice';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Folder,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  Settings,
  Plus,
  Clock,
  X,
} from 'lucide-react';
import type { Project } from '@phrasewright/types';

interface ProjectSelectorProps {
  className?: string;
}

export function ProjectSelector({ className }: ProjectSelectorProps) {
  const dispatch = useAppDispatch();
  const { currentProject, recentProjects, loading } = useAppSelector(
    (state) => state.project
  );
  const { projectSelectorExpanded } = useAppSelector(
    (state) => state.ui.layout
  );
  const [isSelecting, setIsSelecting] = useState(false);

  // Handle File System Access API folder selection
  const handleSelectFolder = useCallback(async () => {
    try {
      setIsSelecting(true);
      const result = await openFolderAndCreateProject();
      if (result.success && result.project) {
        dispatch(setCurrentProject(result.project));
        dispatch(addRecentProject(result.project));
        toast.success(`Opened project: ${result.project.name}`);
      } else if (result.error && result.error !== 'User cancelled') {
        toast.error(`Failed to open project: ${result.error}`);
      }
    } catch (error) {
      toast.error('Failed to open project');
    } finally {
      setIsSelecting(false);
    }
  }, [dispatch]);

  const handleProjectSelect = useCallback(
    (project: Project) => {
      dispatch(setCurrentProject(project));
      dispatch(addRecentProject(project));
    },
    [dispatch]
  );

  const handleRemoveFromRecent = useCallback(
    (projectId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(removeRecentProject(projectId));
    },
    [dispatch]
  );

  if (loading) {
    return (
      <div className={cn('p-4', className)}>
        <Skeleton className="h-8 w-full mb-2" />
        <Skeleton className="h-6 w-3/4" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Card className={cn('border-0 shadow-none bg-transparent', className)}>
        <CardHeader className="px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch(toggleProjectSelector())}
              className="flex items-center gap-2 p-0 h-auto font-medium text-foreground"
            >
              {projectSelectorExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {currentProject ? (
                <>
                  <FolderOpen className="h-4 w-4" />
                  <span className="truncate">{currentProject.name}</span>
                </>
              ) : (
                <>
                  <Folder className="h-4 w-4" />
                  <span>Select Project</span>
                </>
              )}
            </Button>

            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleSelectFolder}
                    disabled={isSelecting}
                    className="h-8 w-8"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Open Folder</p>
                </TooltipContent>
              </Tooltip>

              {currentProject && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>Project Settings</DropdownMenuItem>
                    <DropdownMenuItem>Refresh Files</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive">
                      Close Project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardHeader>

        {projectSelectorExpanded && (
          <CardContent className="px-4 pb-4 pt-0">
            {currentProject && (
              <div className="mb-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="truncate">{currentProject.path}</span>
                  <Badge variant="secondary" className="text-xs">
                    {currentProject.settings.defaultFileType}
                  </Badge>
                </div>
              </div>
            )}

            {recentProjects.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Recent
                  </span>
                </div>
                <div className="space-y-1">
                  {recentProjects.slice(0, 5).map((project) => (
                    <div
                      key={project.id}
                      className={cn(
                        'flex items-center gap-2 p-2 rounded-md text-sm cursor-pointer',
                        'hover:bg-accent transition-colors group',
                        currentProject?.id === project.id && 'bg-accent'
                      )}
                      onClick={() => handleProjectSelect(project)}
                    >
                      <Folder className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 truncate">{project.name}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => handleRemoveFromRecent(project.id, e)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!currentProject && recentProjects.length === 0 && (
              <div className="text-center py-6">
                <Folder className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground mb-3">
                  No projects open
                </p>
                <Button onClick={handleSelectFolder} disabled={isSelecting}>
                  {isSelecting ? 'Selecting...' : 'Open Folder'}
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </TooltipProvider>
  );
}
